import { PanelTabs, panelTabIds } from '../ui/PanelTabs.js';
import { StatusPill } from '../ui/StatusPill.js';
import { useSceneBreakdown } from './context.js';
import { PROGRESS_LABEL, PROGRESS_STATE, progressReasonText } from './labels.js';
import type { ScenePanelContext, ScenePanelTab, ScenePanelTabId } from './panelTabs.js';

/**
 * THE RIGHT PANEL: WHAT THE SCENE REQUIRES, AND THE PRODUCER'S DECISIONS ON IT.
 *
 * Its tabs come from the registry (`panelTabs.ts`); with one tab there is no strip. Under every
 * tab sits the scene's review — Mark scene reviewed, or Not reviewed after all — a plain control,
 * because the page's one red button is "Review next scene": reviewing is the work, and the mark is
 * its bookmark. A scene that needs reviewing again says why, in the server's reason.
 */
export function ProductionPanel({
  tabs,
  view,
  onView,
  ctx,
  onWholePlan,
}: {
  tabs: readonly ScenePanelTab[];
  view: ScenePanelTabId;
  onView: (view: ScenePanelTabId) => void;
  ctx: ScenePanelContext;
  /** The whole screenplay's production plan, when it is on. */
  onWholePlan?: () => void;
}) {
  const { detail, detailLoading, detailError, retryDetail, markReviewed, unmarkReviewed, busy, refusal, error, clearError } =
    useSceneBreakdown();
  const active = tabs.find((tab) => tab.id === view) ?? tabs[0];
  const ids = panelTabIds('el-panel', active?.id ?? 'breakdown');

  return (
    <div className="el-panel" data-testid="el-panel">
      <PanelTabs
        tabs={tabs.map((tab) => ({ id: tab.id, label: tab.label }))}
        value={active?.id ?? 'breakdown'}
        onChange={onView}
        label="How to look at this scene"
        idPrefix="el-panel"
        testIdPrefix="el-panel-tabs"
        end={
          onWholePlan === undefined ? undefined : (
            <button type="button" className="el-link" data-testid="el-panel-whole-plan" onClick={onWholePlan}>
              Whole production plan
            </button>
          )
        }
      />

      <div
        className="el-panel-scroll"
        role={tabs.length > 1 ? 'tabpanel' : undefined}
        id={ids.panel}
        aria-labelledby={tabs.length > 1 ? ids.tab : undefined}
        data-testid={`el-panel-view-${active?.id ?? 'breakdown'}`}
      >
        {error === null ? null : (
          <div className="el-error" role="alert" data-testid="el-error">
            <span>{error}</span>
            <button type="button" className="el-link" onClick={clearError}>
              Dismiss
            </button>
          </div>
        )}

        {active !== undefined && active.id !== 'breakdown' ? (
          // The plan's views read the plan, not the scene's breakdown answer.
          active.render(ctx)
        ) : detailError !== null ? (
          <div className="el-error" role="alert" data-testid="el-detail-error">
            <span>{detailError}</span>
            <button type="button" className="ctl" onClick={retryDetail}>
              Try again
            </button>
          </div>
        ) : detail === null || detailLoading ? (
          <PanelSkeleton />
        ) : (
          active?.render(ctx)
        )}
      </div>

      {detail === null ? null : (
        <footer className="el-panel-foot" data-testid="el-review-foot">
          <div className="el-panel-foot-state">
            <StatusPill
              state={PROGRESS_STATE[detail.progress]}
              label={PROGRESS_LABEL[detail.progress]}
              size="sm"
              testId="el-review-progress"
            />
            {detail.progress === 'NEEDS_RE_REVIEW' ? (
              <span className="el-panel-foot-reason" data-testid="el-review-reason">
                {progressReasonText(detail.progressReason)}
              </span>
            ) : null}
          </div>
          {detail.progress === 'REVIEWED' ? (
            <button
              type="button"
              className="ctl"
              data-testid="el-unmark-reviewed"
              disabled={busy || refusal !== null}
              title={refusal ?? undefined}
              onClick={unmarkReviewed}
            >
              Not reviewed after all
            </button>
          ) : (
            <button
              type="button"
              className="ctl el-mark-reviewed"
              data-testid="el-mark-reviewed"
              disabled={busy || refusal !== null}
              title={refusal ?? undefined}
              onClick={markReviewed}
            >
              Mark scene reviewed
            </button>
          )}
        </footer>
      )}
    </div>
  );
}

function PanelSkeleton() {
  return (
    <div className="el-skeleton" role="status" aria-live="polite" data-testid="el-panel-loading">
      <span className="sr-only">Loading the scene’s breakdown…</span>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <span key={i} className="el-skeleton-row" aria-hidden="true" />
      ))}
    </div>
  );
}
